import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { settingsStorage } from '../utils/settingsPersistence';

/**
 * Viewport Colors State (Settings › Color)
 *
 * Per-workbench viewport canvas & grid color OVERRIDES, deliberately
 * independent from the GUI theme: the editor pins explicit values as inline
 * CSS custom properties on :root (applyViewportColorOverrides), and every
 * viewport resolves them through the mode-scoped tokens in global.css. An
 * absent override falls through the token sheet, so an untouched mode keeps
 * following the color mode (light/dark) exactly as before.
 */

/** Workbench view modes with editable viewport colors (mirrors AppMode). */
export type ViewportColorMode = '2d' | '3d' | 'bim' | 'sketch';

/** Editable viewport color slots (exposed in the Settings › Color pane). */
export type ViewportColorKey = 'background' | 'gridMajor' | 'gridSection';

/**
 * Slot → mode-scoped CSS custom property (global.css token sheet). Each
 * sheet aliases the shared viewport tokens, so an unpinned slot keeps
 * following the color mode; the editor's inline :root values win over the
 * alias and are removed again on reset.
 */
export interface ViewportColorTokenSheet {
    background: string;
    gridMajor: string;
    /** Non-editable companion token (no canvas consumer draws it today). */
    gridMinor: string;
    gridSection: string;
}

export const VIEWPORT_COLOR_TOKENS: Record<ViewportColorMode, ViewportColorTokenSheet> = {
    '2d': {
        background: '--color-bg-viewport-2d',
        gridMajor: '--color-grid-major-2d',
        gridMinor: '--color-grid-minor-2d',
        gridSection: '--color-grid-section-2d',
    },
    '3d': {
        background: '--color-bg-viewport-3d',
        gridMajor: '--color-grid-major-3d',
        gridMinor: '--color-grid-minor-3d',
        gridSection: '--color-grid-section-3d',
    },
    bim: {
        background: '--color-bg-viewport-bim',
        gridMajor: '--color-grid-major-bim',
        gridMinor: '--color-grid-minor-bim',
        gridSection: '--color-grid-section-bim',
    },
    sketch: {
        background: '--color-bg-viewport-sketch',
        gridMajor: '--color-grid-major-sketch',
        gridMinor: '--color-grid-minor-sketch',
        gridSection: '--color-grid-section-sketch',
    },
};

const EDITABLE_KEYS: ViewportColorKey[] = ['background', 'gridMajor', 'gridSection'];

/** Per-mode overrides; a missing slot falls back to the theme tokens. */
export type ViewportColorOverrides = Partial<Record<ViewportColorKey, string>>;

interface ViewportColorsState {
    overrides: Record<ViewportColorMode, ViewportColorOverrides>;
    /** Bumped on every mutation so canvas consumers can re-resolve tokens. */
    revision: number;
    setColor: (mode: ViewportColorMode, key: ViewportColorKey, value: string) => void;
    resetMode: (mode: ViewportColorMode) => void;
    resetAll: () => void;
}

const EMPTY_OVERRIDES: Record<ViewportColorMode, ViewportColorOverrides> = {
    '2d': {},
    '3d': {},
    bim: {},
    sketch: {},
};

export const useViewportColorsStore = create<ViewportColorsState>()(
    persist(
        (set) => ({
            overrides: { ...EMPTY_OVERRIDES },
            revision: 0,

            setColor: (mode, key, value) =>
                set((state) => ({
                    overrides: {
                        ...state.overrides,
                        [mode]: { ...state.overrides[mode], [key]: value },
                    },
                    revision: state.revision + 1,
                })),

            resetMode: (mode) =>
                set((state) => ({
                    overrides: { ...state.overrides, [mode]: {} },
                    revision: state.revision + 1,
                })),

            resetAll: () =>
                set((state) => ({
                    overrides: { ...EMPTY_OVERRIDES },
                    revision: state.revision + 1,
                })),
        }),
        {
            name: 'yap-viewport-colors',
            storage: settingsStorage,
            partialize: (state) => ({ overrides: state.overrides }),
        }
    )
);

/**
 * Pin (or clear) the editor-owned inline custom properties on :root. Inline
 * values win the cascade over the stylesheet aliases; removing them hands
 * the slot back to the token sheet (theme-following default).
 */
export function applyViewportColorOverrides(
    overrides: Record<ViewportColorMode, ViewportColorOverrides>
): void {
    const style = document.documentElement.style;
    for (const mode of Object.keys(VIEWPORT_COLOR_TOKENS) as ViewportColorMode[]) {
        const modeOverrides = overrides[mode] ?? {};
        for (const key of EDITABLE_KEYS) {
            const token = VIEWPORT_COLOR_TOKENS[mode][key];
            const value = modeOverrides[key];
            if (value) style.setProperty(token, value);
            else style.removeProperty(token);
        }
    }
}

// Keep the :root tokens in sync with the store — including the synchronous
// localStorage rehydration that already ran during store creation. Every
// viewport reads these tokens (CSS classes and useViewportThemeColors), so
// this one subscription drives all workbenches live.
applyViewportColorOverrides(useViewportColorsStore.getState().overrides);
useViewportColorsStore.subscribe((state) => applyViewportColorOverrides(state.overrides));
